import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiPost } from '../lib/api';
import { brand, featureFlags, resolveContactPhone } from '../config/esanzaContent';
import { useWebsiteContent } from '../hooks/useWebsiteContent';
import TurnstileWidget, { isTurnstileEnabled } from './security/TurnstileWidget';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle');
  const formStartedAt = useRef(Date.now());
  const { settings, pages } = useWebsiteContent();
  const phone = resolveContactPhone(settings);
  const marketplaceVisible = Boolean(settings.marketplace_visible ?? featureFlags.marketplaceVisible);
  const isPageVisible = (pageKey: string) => pages[pageKey]?.visible !== false;

  const subscribe = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    if (isTurnstileEnabled && !turnstileToken) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      await apiPost('/api/public/newsletter', {
        email,
        website,
        form_started_at: formStartedAt.current,
        turnstile_token: turnstileToken,
      });
      setEmail('');
      setWebsite('');
      setTurnstileToken('');
      formStartedAt.current = Date.now();
      setStatus('ok');
    } catch {
      setStatus('error');
    }
  };

  const handleVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const clearVerification = useCallback(() => {
    setTurnstileToken('');
  }, []);

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-[#06150e] text-white">
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-80 w-80 rounded-full bg-[#d4af37]/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 left-[-10%] h-80 w-80 rounded-full bg-[#14532d]/30 blur-[140px]" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-2 lg:grid-cols-[1.35fr_0.9fr_0.9fr_1fr] lg:px-8">
        <div>
          <img src={brand.logo} alt="eSanza" className="h-14 w-auto" />
          <p className="mt-5 max-w-md text-sm leading-7 text-white/68">
            {t(
              'footer.tagline',
              'AI-powered farm management, traceability and market infrastructure for modern agriculture.'
            )}
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#f3d978]">
            {brand.company}
          </p>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white/50">{t('footer.navigate', 'Navigate')}</h3>
          <div className="mt-5 grid gap-3 text-sm">
            {isPageVisible('platform') && <Link to="/platform" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.platform', 'Platform')}</Link>}
            <Link to="/field-intelligence" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.fieldIntelligence', 'Field Intelligence')}</Link>
            {isPageVisible('traceability') && <Link to="/traceability" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.traceability', 'Traceability')}</Link>}
            {isPageVisible('impact') && <Link to="/impact" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.impact', 'Impact')}</Link>}
            {marketplaceVisible && isPageVisible('marketplace') && <Link to="/marketplace" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.marketplace', 'Marketplace')}</Link>}
            {isPageVisible('contact') && <Link to="/contact" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.contact', 'Contact')}</Link>}
          </div>
        </div>

<div>
            <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white/50">{t('footer.company', 'Company')}</h3>
            <div className="mt-5 grid gap-3 text-sm">
              {isPageVisible('about') && <Link to="/about" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.about', 'About')}</Link>}
              {marketplaceVisible && isPageVisible('marketplace') && <Link to="/marketplace" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.marketplace', 'Marketplace')}</Link>}
              <Link to="/privacy" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.privacy', 'Privacy Policy')}</Link>
              <Link to="/delete-account" className="text-white/75 transition hover:translate-x-1 hover:text-[#f3d978]">{t('nav.deleteAccount', 'Delete Account')}</Link>
              <a href={`mailto:${brand.email}`} className="text-white/75 transition hover:text-[#f3d978]">{brand.email}</a>
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-white/75 transition hover:text-[#f3d978]">{phone}</a>
            </div>
          </div>

        <div className="lg:pl-6">
          <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white/50">{t('footer.updates', 'Updates')}</h3>
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.05] p-5 backdrop-blur">
            <p className="text-sm leading-6 text-white/65">
              {t('footer.newsletter_note', 'Get product updates, field intelligence insights and eSanza ecosystem news.')}
            </p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2">
              <input
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                className="hidden"
                aria-hidden="true"
                name="website"
              />
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('footer.email_placeholder', 'Email address')}
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#d4af37]"
              />
              <button
                className="shrink-0 rounded-xl bg-[#d4af37] px-5 py-3 text-sm font-extrabold text-[#06150e] transition hover:bg-[#f0d36c]"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? t('footer.sending', 'Sending...') : t('footer.join', 'Join')}
              </button>
            </form>
            <div className="mt-3">
              <TurnstileWidget action="newsletter" onVerify={handleVerify} onExpire={clearVerification} />
            </div>
            {status === 'ok' && <p className="mt-3 text-xs text-emerald-300">{t('footer.success', 'Thank you. We saved your email.')}</p>}
            {status === 'error' && <p className="mt-3 text-xs text-red-300">{turnstileToken || !isTurnstileEnabled ? t('footer.error', { email: brand.email, defaultValue: `Could not submit. Please email ${brand.email}.` }) : 'Please complete the verification first.'}</p>}
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 px-5 py-6">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 text-xs text-white/45 md:flex-row lg:px-8">
          <p>© {new Date().getFullYear()} {brand.name}. {t('footer.rights', 'All rights reserved.')}</p>
          <p className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
            {t('footer.built', 'Built in Rwanda. Designed for global agriculture.')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
